import { uiText } from "@/lib/i18n/uiText";
import { Link, useLocation } from "@/lib/router-compat";
import { equityLinksFor, isEquitySource } from "@/lib/seo/linkEquity";
import { resolveInternalHref } from "@/lib/seo/internalHref";

/**
 * In-content contextual link block rendered on the curated equity-source pages
 * (home, /predictions, /ai-predictions, /high-odds, /blog, /guides).
 *
 * Unlike the sitewide footer grid, these are prose links with descriptive
 * anchor text, which is what actually passes topical authority to the weaker
 * URLs in our link graph. Each source page gets a different rotation of the
 * target pool (see `src/lib/seo/linkEquity.ts`).
 */
export function ContextualEquityLinks({ count = 8 }: { count?: number }) {
  const { pathname } = useLocation();
  if (!isEquitySource(pathname)) return null;

  const links = equityLinksFor(pathname, count);
  if (links.length === 0) return null;

  return (
    <section aria-labelledby="equity-links-heading" className="space-y-3">
      <h2 id="equity-links-heading" className="text-xl font-semibold">
         {uiText("Where to go next on Statlign")} </h2>
      <ul className="grid grid-cols-1 gap-2 text-sm text-muted-foreground sm:grid-cols-2">
        {links.map((l) => (
          <li key={l.path} className="leading-relaxed">
            {l.context}{" "}
            <Link to={resolveInternalHref(l.path)} className="font-medium text-primary hover:underline">
              {uiText(l.label)}
            </Link>
            .
          </li>
        ))}
      </ul>
    </section>
  );
}
